import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';

import { useLeagueLink, useLogout } from '../api/hooks.js';
import { signIn, useCan, useCurrentUser } from '../auth.js';
import { Logo } from './Logo.js';

const SECTIONS = [
  { to: '/', label: 'Home', end: true },
  { to: '/news', label: 'News', end: false },
  { to: '/polls', label: 'Polls', end: false },
  { to: '/rules', label: 'Rules', end: false },
  { to: '/standings', label: 'Standings', end: false },
  { to: '/stats', label: 'Stats', end: false },
  { to: '/chats', label: 'Chats', end: false },
];

/**
 * The league itself, over on Sleeper — where the rosters, the waiver wire and
 * the actual playing happen. It leaves the site, so it is an anchor rather than
 * a route and says as much.
 */
function SleeperLink({ className }: { className: string }) {
  const league = useLeagueLink();
  if (!league) return null;

  return (
    <a
      href={league.url}
      target="_blank"
      rel="noreferrer"
      className={className}
      title={`The ${league.season} league on Sleeper`}
    >
      <span>
        Sleeper <span aria-hidden>↗</span>
      </span>
    </a>
  );
}

function AccountMenu() {
  const user = useCurrentUser();
  const isAdmin = useCan('admin');
  const logout = useLogout();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  if (!user) {
    return (
      <button type="button" className="btn-primary" onClick={() => signIn()}>
        Log in
      </button>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className="btn-secondary"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        {user.avatarUrl ? (
          <img src={user.avatarUrl} alt="" className="h-5 w-5 rounded-full" />
        ) : null}
        <span className="max-w-[10rem] truncate">{user.displayName}</span>
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-56 overflow-hidden border-2 border-brass-400
            bg-field-900"
        >
          <div className="border-b border-field-800 bg-field-950 px-4 py-3">
            <p className="truncate font-display text-sm font-semibold uppercase tracking-bug text-field-50">
              {user.displayName}
            </p>
            <p className="truncate text-xs text-field-400">{user.email}</p>
          </div>
          <Link
            to="/settings"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block px-4 py-2.5 text-sm text-field-200 hover:bg-field-800"
          >
            Settings
          </Link>
          {isAdmin ? (
            <Link
              to="/admin"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="block px-4 py-2.5 text-sm text-field-200 hover:bg-field-800"
            >
              Website settings
            </Link>
          ) : null}
          <button
            type="button"
            role="menuitem"
            className="block w-full px-4 py-2.5 text-left text-sm text-field-200 hover:bg-field-800"
            onClick={() => {
              logout.mutate(undefined, { onSuccess: () => window.location.assign('/') });
            }}
          >
            Log out
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function Layout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // Close the mobile menu whenever navigation happens.
  useEffect(() => setMenuOpen(false), [location.pathname]);

  return (
    <div className="flex min-h-screen flex-col">
      {/*
        The header is the scorebar: a fixed-height black bug pinned to the top,
        the crest boxed in volt at its left end, the sections raked across it,
        and a hard volt rule closing it off from the page.
      */}
      <header className="sticky top-0 z-40 border-b-2 border-brass-400 bg-field-950">
        <div className="mx-auto flex h-16 max-w-[100rem] items-stretch px-4 sm:px-6 lg:px-10">
          <Link to="/" className="group -ml-2 flex items-center gap-3 pr-4 sm:-ml-3">
            {/*
              The crest is dark artwork with a pale rim, so it wants a dark
              plate: knocked into a solid volt square it lost its own outline
              and read as a smudge. A volt keyline gives the scorebar its
              accent without fighting the shield.
            */}
            <span
              className="flex h-16 w-14 items-center justify-center border-x-2 border-brass-400
                bg-field-900 sm:w-16"
            >
              <Logo title="" className="h-12 w-auto shrink-0 sm:h-[3.25rem]" />
            </span>
            <span className="font-display text-lg font-bold uppercase leading-none tracking-tight text-field-50 sm:text-xl">
              Gentlemen&apos;s
              <span className="block text-brass-400">League</span>
            </span>
          </Link>

          <nav className="ml-auto hidden items-stretch md:flex">
            {SECTIONS.map((section) => (
              <NavLink
                key={section.to}
                to={section.to}
                end={section.end}
                className={({ isActive }) => `nav-tab ${isActive ? 'nav-tab-on' : ''}`}
              >
                <span>{section.label}</span>
              </NavLink>
            ))}
            <SleeperLink className="nav-tab text-field-400" />
          </nav>

          <div className="ml-auto flex items-center pl-3 md:ml-6">
            <AccountMenu />
          </div>

          <button
            type="button"
            className="btn-ghost px-2 md:hidden"
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((value) => !value)}
          >
            ☰
          </button>
        </div>

        {menuOpen ? (
          <nav className="border-t border-field-800 bg-field-900 px-4 pb-3 pt-1 md:hidden">
            {SECTIONS.map((section) => (
              <NavLink
                key={section.to}
                to={section.to}
                end={section.end}
                className={({ isActive }) =>
                  `block border-l-4 px-3 py-2.5 font-display text-sm font-semibold uppercase
                   tracking-bug ${
                     isActive
                       ? 'border-brass-400 bg-field-800 text-brass-300'
                       : 'border-transparent text-field-300'
                   }`
                }
              >
                {section.label}
              </NavLink>
            ))}
            <SleeperLink
              className="block border-l-4 border-transparent px-3 py-2.5 font-display text-sm
                font-semibold uppercase tracking-bug text-field-400"
            />
          </nav>
        ) : null}
      </header>

      <main className="mx-auto w-full max-w-[100rem] flex-1 px-4 pb-12 sm:px-6 lg:px-10">
        <Outlet />
      </main>

      <footer className="mt-auto border-t-2 border-brass-400 bg-field-900">
        <div
          className="mx-auto flex max-w-[100rem] flex-wrap items-center justify-between gap-4 px-4
            py-6 sm:px-6 lg:px-10"
        >
          <div className="flex items-center gap-3">
            <Logo title="" className="h-11 w-auto shrink-0" />
            <span className="font-display text-sm font-semibold uppercase tracking-bug text-field-300">
              Gentlemen&apos;s League
            </span>
          </div>
          <span className="font-display text-xs font-semibold uppercase tracking-bug text-field-600">
            Dynasty fantasy football · Est. 2024
          </span>
        </div>
      </footer>
    </div>
  );
}
